import './App.css';
import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useLocalStorage, useMount, useLocation } from 'react-use';
import {
  useRecoilState, useSetRecoilState,
} from 'recoil';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { AnimatePresence, motion } from 'framer-motion';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import routes from '../routes/index';
import {
  tokenAtom,
  userAtom,
  userPrivilagesAtom,
  userOrganizationsAtom
} from '../atoms/Atoms';
import ProtectedRoute from '../routes/ProtectedRoutes';
import Header from './shared-components/header/Header';
import Footer from './shared-components/footer/Footer';
import Notifications from './shared-components/notifications/Notifications';
import envConfig from '../envConfig';
import MenuSideBar from './shared-components/menu-sidebar/MenuSideBar';
import api from '../api/api';
import { Spinner } from './shared-components/Spinner';
import { backdropVariants } from '../utils';
import { encryptString } from '../functions';
import LeadModal from './pages/admin/shared-components/LeadModal';
import LeadLogsModal from './pages/admin/shared-components/LeadLogsModal';
import LeadAssignedModal from './pages/admin/shared-components/LeadAssignedModal';
import NewLeadNoteModal from './pages/admin/shared-components/NewLeadNoteModal';
import LeadFilesModal from './pages/admin/shared-components/LeadFilesModal';
import LeadAppointmentsModal from './pages/admin/shared-components/LeadAppointmentsModal';
import LeadContactsModal from './pages/admin/shared-components/LeadContactsModal';
import EditContactModal from './pages/admin/shared-components/EditContactModal';
import MarkLostModal from './pages/admin/shared-components/MarkLostModal';
import LeadAssignOrganizationModal from './pages/admin/shared-components/LeadAssignOrganizationModal';
import LeadPayoutDateModal from './pages/admin/shared-components/LeadPayoutDateModal';
import LeadCommissionsModal from './pages/admin/shared-components/LeadCommissionsModal';
import WattsSoldModal from './pages/admin/leads/shared-components/WattsSoldModal';

function App() {
  if (!String.prototype.format) {
    String.prototype.format = function () {
      const args = arguments;
      return this.replace(/{(\d+)}/g, function (match, number) { 
        return typeof args[number] !== 'undefined'
          ? args[number]
          : match;
      });
    };
  }
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const setToken = useSetRecoilState(tokenAtom);
  const setUserPrivilages = useSetRecoilState(userPrivilagesAtom);
  const setUserOrganizations = useSetRecoilState(userOrganizationsAtom);
  const [language] = useLocalStorage(`${envConfig.StoragePrefix}-lang`);
  const [token] = useLocalStorage(`${envConfig.StoragePrefix}-token`);
  const [storageTokenDate] = useLocalStorage(`${envConfig.StoragePrefix}-token-date`);
  const [storageUser] = useLocalStorage(`${envConfig.StoragePrefix}-user`);
  const [user, setUser] = useRecoilState(userAtom);
  const [fetchedState, setFetchedState] = useState(false);
  const [initializing, setInitializing] = useState(true);

  function updateLanguage() {
    if (user) {
      api.post('user/profile', {
        preferredLanguage: i18n.language === 'en' ? 0 : 1
      });
    }
  }

  function getCurrentUserState() {
    if (user && !fetchedState) {
      setFetchedState(true);
      api.get('auth/refreshstate')
        .then((response) => {
          if (response.data.data.isBlocked) {
            window.localStorage.removeItem(`${envConfig.StoragePrefix}-user`);
            window.localStorage.removeItem(`${envConfig.StoragePrefix}-token`);
            setToken(null);
            setUser(null);
            window.location = '/';
          } else {
            setUser(response.data.data);
            localStorage.setItem(
              `${envConfig.StoragePrefix}-user`,
              JSON.stringify(response.data.data)
            );
          }
        }).catch((err) => {
          window.localStorage.removeItem(`${envConfig.StoragePrefix}-user`);
          window.localStorage.removeItem(`${envConfig.StoragePrefix}-token`);
          setToken(null);
          setUser(null);
          window.location = '/';
        });
    }
    if (user) {
      api.get('auth/refreshtoken')
        .then((response) => {
          setToken(response.data.data);
          localStorage.setItem(
            `${envConfig.StoragePrefix}-token-date`,
            dayjs().toString()
          );
        }).catch((err) => {
          // console.log('error getting token');
        });
    }
  }

  function getUserOrganizations() {
    api.get('organizations/user')
    .then((response) => {
      if (response?.data?.success) {
        setUserOrganizations(response.data.data);
      }
    })
    .catch((error) => {
      console.log(error?.message);
    });
  }

  useEffect(() => {
    getCurrentUserState();
    if (user) {
      getUserOrganizations();
      setUserPrivilages({
        hasGlobalPrivilages: _.filter(user?.roles, (r) => r === 'GlobalAdmin').length >= 1,
        hasAdminPrivilages: _.filter(user?.roles, (r) => r === 'GlobalAdmin' || r === 'Admin').length >= 1,
        hasCustomerSupportPrivilages: _.filter(user?.roles, (r) => r === 'GlobalAdmin' || r === 'Admin' || r === 'CustomerSupport').length >= 1,
        hasOrganizationAdminPrivilages: _.filter(user?.roles, (r) => r === 'GlobalAdmin' || r === 'Admin' || r === 'OrganizationAdmin').length >= 1,
        hasCloserAdminPrivilages: _.filter(user?.roles, (r) => r === 'GlobalAdmin' || r === 'Admin' || r === 'CloserAdmin' || r === 'OrganizationAdmin').length >= 1,
        hasCloserManagerPrivilages: _.filter(user?.roles, (r) => r === 'GlobalAdmin' || r === 'Admin' || r === 'CloserAdmin' || r === 'CloserManager' || r === 'OrganizationAdmin').length >= 1,
        hasCloserPrivilages: _.filter(user?.roles, (r) => r === 'GlobalAdmin' || r === 'CustomerSupport' || r === 'Admin' || r === 'CloserAdmin' || r === 'CloserManager' || r === 'Closer' || r === 'OrganizationAdmin').length >= 1,
        hasSetter: _.filter(user?.roles, (r) => r === 'Setter').length >= 1,
        hasCloser: _.filter(user?.roles, (r) => r === 'Closer').length >= 1,
        hasOnlySetter: user?.roles?.length === 1 && _.filter(user?.roles, (r) => r === 'Setter').length >= 1,
        hasLeadManager: _.filter(user?.roles, (r) => r === 'LeadManager').length >= 1,
        hasLearningManager: _.filter(user?.roles, (r) => r === 'GlobalAdmin' || r === 'Admin' || r === 'CustomerSupport' || r === 'LearningManager').length >= 1,
        hasLearning: _.filter(user?.roles, (r) => r === 'LearningManager' || r === 'Learning').length >= 1
      });
    } else {
      setUserOrganizations([]);
    }
  }, [user]);

  useEffect(() => {
    updateLanguage();
  }, [language, i18n.language, user]);

  useEffect(() => {
    setTimeout(() => setInitializing(false), 1500);
  }, []);
 
  useMount(() => {
    if (language) {
      localStorage.setItem(`${envConfig.StoragePrefix}-lang`, JSON.stringify(language))
      i18n.changeLanguage(language);
    } else {
      i18n.changeLanguage(envConfig.DefaultLanguage);
      localStorage.setItem(`${envConfig.StoragePrefix}-lang`, JSON.stringify(envConfig.DefaultLanguage))
    }

    let forceLogout = false;
    if (storageTokenDate) {
      const tokenLifetimeHours = parseInt(envConfig.TokenLifetimeHours, 10);
      const lastDate = dayjs(storageTokenDate);
      const hourDifference = dayjs().diff(lastDate, 'hour');
      forceLogout = hourDifference >= tokenLifetimeHours;
    }

    if (forceLogout) {
      window.localStorage.removeItem(`${envConfig.StoragePrefix}-user`);
      window.localStorage.removeItem(`${envConfig.StoragePrefix}-token`);
      setToken(null);
      setUser(null);
      window.location = '/';
    } else {
      if (storageUser) {
        setUser(storageUser);
      }
  
      if (token) {
        setToken(token);
      }
    }
  });

  return (
    <div className="wrapper antialiased overflow-auto">
      <AnimatePresence mode="wait" initial={false}>
        <Router>
          <Header key="header" />
          <div className="" key="body">
            <Switch location={location} key={location.pathname}>
              {_.map(routes, (route) => {
                if (route.protected) {
                  return (
                    <ProtectedRoute
                      key={route.id}
                      path={route.path}
                      exact={route.exact}
                      component={route.component}
                    />
                  );
                }
                return (
                  <Route
                    key={route.id}
                    path={route.path}
                    exact={route.exact}
                    component={route.component}
                  />
                );
              })}
            </Switch>
          </div>
          <Footer key="footer" />
        </Router>
        <LeadModal />
        <LeadPayoutDateModal />
        <LeadCommissionsModal />
        <LeadLogsModal />
        <NewLeadNoteModal />
        <LeadFilesModal />
        <LeadAppointmentsModal />
        <LeadContactsModal />
        <LeadAssignedModal />
        <LeadAssignOrganizationModal />
        <EditContactModal />
        <MarkLostModal />
        <WattsSoldModal />
        
        <Notifications key="notif" />
        <MenuSideBar />

        <motion.div
          initial="visible"
          animate="hidden"
          exit="visible"
          className={`${initializing ? 'fixed' : 'hidden'} fixed bg-white inset-0 z-20`}
        >
          <div className="bg-white flex h-full loading-screen-full-white">
            <Spinner className="text-accentColor mx-auto my-auto" big />
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default App;
